/**
 * XLSX export utilities for table data.
 *
 * Uses backend endpoint for downloads (works on all platforms including
 * iOS Safari and PWA). Client-side generation is used only for email
 * attachment base64.
 */

import api from '../lib/api'
import type { TableData } from '../types/tableData'

/**
 * Download table data as XLSX via backend endpoint.
 * Uses fetch + blob approach with proper Content-Disposition from server,
 * which works reliably on all platforms including mobile and PWA.
 */
export async function downloadTableAsXlsx(data: TableData): Promise<void> {
  const exportCols = data.export_columns ?? data.columns
  const response = await api.post(
    '/api/export/table-xlsx/',
    {
      columns: exportCols,
      rows: data.rows,
      title: data.title,
      // Propaga o campo de cor por linha (backend aplica PatternFill).
      // Ausência mantém o comportamento atual (apenas header colorido).
      ...(data.row_color_field
        ? { row_color_field: data.row_color_field }
        : {}),
      // Lista opcional de chaves de colunas a tintar (ex: ["status"]).
      // Ausência = pinta toda a linha (backcompat).
      ...(data.row_color_columns && data.row_color_columns.length > 0
        ? { row_color_columns: data.row_color_columns }
        : {}),
    },
    { responseType: 'blob' }
  )

  const blob = response.data as Blob
  const url = URL.createObjectURL(blob)

  // Open the blob URL — browser handles download via Content-Disposition
  window.open(url, '_self')
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

/**
 * Generate XLSX as base64 string (for email attachment — client-side).
 *
 * Usa `write-excel-file` em vez de `xlsx` (sheetjs): a versão CE de `xlsx`
 * carrega CVEs high sem fix upstream (Prototype Pollution + ReDoS —
 * GHSA-4r6h-8v6p-xvw6 e GHSA-5pgg-2g8v-p4x9). `write-excel-file` é lightweight
 * e sem deps transitivas problemáticas.
 */
export async function generateXlsxBase64(data: TableData): Promise<string> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser')

  const exportCols = data.export_columns ?? data.columns

  // Primeira linha = header (bold); demais = dados coercidos pra string.
  // Formato SheetData = Cell[][] (array de linhas, cada linha é array de cells).
  type Cell = { value: string; fontWeight?: 'bold' }
  const header: Cell[] = exportCols.map(col => ({
    value: col.label,
    fontWeight: 'bold',
  }))
  const body: Cell[][] = data.rows.map(row =>
    exportCols.map(col => {
      const value = row[col.key]
      if (value === null || value === undefined) return { value: '' }
      return { value: String(value) }
    })
  )
  const sheetData: Cell[][] = [header, ...body]

  // Excel proíbe `: \ / ? * [ ]` em nome de sheet e limita a 31 chars.
  const sheetName =
    data.title.slice(0, 31).replace(/[:\\/?*[\]]/g, '-') || 'Sheet1'

  const result = await writeXlsxFile(sheetData, { sheet: sheetName })
  const blob = await result.toBlob()

  // Blob → base64 (sem o prefixo "data:...;base64,").
  const buffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunkSize))
    )
  }
  return btoa(binary)
}
